import { supabase } from "../../lib/supabase";
import type {
  Employee,
  EmployeeRole,
  PayPeriod,
  DailySales,
  DailyTip,
  TipEvent,
  KitchenTipout,
  TipsSetting,
  ImportLog,
  CoachDailyTip,
  CoachEvent,
  CoachEventAssignment,
  SquareTipShare,
  SquareTipShareEntry,
} from "./types";

/**
 * Tips tables live in the `tips` schema, exposed to PostgREST via
 * auto-updatable views in `public` with a `tips_` prefix.
 * See migration 0013_tips_public_views.sql.
 */

// ── Employees ──────────────────────────────────────────────

export async function fetchEmployees(role?: EmployeeRole): Promise<Employee[]> {
  let q = supabase.from("tips_employees").select("*").order("name");
  if (role) q = q.contains("roles", [role]);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function upsertEmployee(
  emp: Partial<Employee> & { name: string; roles: EmployeeRole[] },
): Promise<Employee> {
  const payload = { ...emp, updated_at: new Date().toISOString() };
  const { data, error } = await supabase
    .from("tips_employees")
    .upsert(payload, { onConflict: "id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Soft-deactivate. Never deletes the row or related tip history. */
export async function deactivateEmployee(id: string): Promise<void> {
  const { error } = await supabase
    .from("tips_employees")
    .update({
      is_active: false,
      end_date: new Date().toISOString().slice(0, 10),
      updated_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function reactivateEmployee(id: string): Promise<void> {
  const { error } = await supabase
    .from("tips_employees")
    .update({ is_active: true, end_date: null, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

// ── Pay Periods ────────────────────────────────────────────

export async function fetchPayPeriods(): Promise<PayPeriod[]> {
  const { data, error } = await supabase
    .from("tips_pay_periods")
    .select("*")
    .order("start_date", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function createPayPeriod(startDate: string, endDate: string, notes?: string): Promise<PayPeriod> {
  const { data, error } = await supabase
    .from("tips_pay_periods")
    .insert({ start_date: startDate, end_date: endDate, notes })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function togglePayPeriodLock(id: string, locked: boolean): Promise<void> {
  const { error } = await supabase
    .from("tips_pay_periods")
    .update({ is_locked: locked })
    .eq("id", id);
  if (error) throw error;
}

// ── Daily Sales ────────────────────────────────────────────

export async function fetchDailySales(from: string, to: string): Promise<DailySales[]> {
  const { data, error } = await supabase
    .from("tips_daily_sales")
    .select("*")
    .gte("date", from)
    .lte("date", to)
    .order("date");
  if (error) throw error;
  return data ?? [];
}

export async function upsertDailySales(date: string, totalSales: number, notes?: string): Promise<DailySales> {
  const { data, error } = await supabase
    .from("tips_daily_sales")
    .upsert(
      { date, total_sales: totalSales, notes, updated_at: new Date().toISOString() },
      { onConflict: "date" },
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Daily Tips ─────────────────────────────────────────────

export async function fetchDailyTips(from: string, to: string): Promise<DailyTip[]> {
  const { data, error } = await supabase
    .from("tips_daily_tips")
    .select("*")
    .gte("date", from)
    .lte("date", to)
    .order("date");
  if (error) throw error;
  return data ?? [];
}

export async function upsertDailyTip(tip: {
  date: string;
  employee_id: string;
  hours_worked: number;
  raw_tips: number;
  calculated_tips: number | null;
}): Promise<DailyTip> {
  const { data, error } = await supabase
    .from("tips_daily_tips")
    .upsert(
      { ...tip, updated_at: new Date().toISOString() },
      { onConflict: "date,employee_id" },
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function upsertDailyTipsBatch(tipRows: Array<{
  date: string;
  employee_id: string;
  hours_worked: number;
  raw_tips: number;
  calculated_tips: number | null;
}>): Promise<void> {
  const payload = tipRows.map((t) => ({ ...t, updated_at: new Date().toISOString() }));
  const { error } = await supabase
    .from("tips_daily_tips")
    .upsert(payload, { onConflict: "date,employee_id" });
  if (error) throw error;
}

// ── Events ─────────────────────────────────────────────────

export async function fetchEvents(from: string, to: string): Promise<TipEvent[]> {
  const { data, error } = await supabase
    .from("tips_events")
    .select("*")
    .gte("date", from)
    .lte("date", to)
    .order("date");
  if (error) throw error;
  return data ?? [];
}

export async function upsertEvent(evt: {
  id?: string;
  date: string;
  name: string;
  total_gratuity: number;
  kitchen_tipout: number;
  notes?: string | null;
}): Promise<TipEvent> {
  const { data, error } = await supabase
    .from("tips_events")
    .upsert(evt, { onConflict: "id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEvent(id: string): Promise<void> {
  const { error } = await supabase.from("tips_events").delete().eq("id", id);
  if (error) throw error;
}

// ── Kitchen Tipouts ────────────────────────────────────────

export async function fetchKitchenTipouts(from: string, to: string): Promise<KitchenTipout[]> {
  const { data, error } = await supabase
    .from("tips_kitchen_tipouts")
    .select("*")
    .gte("date", from)
    .lte("date", to)
    .order("date");
  if (error) throw error;
  return data ?? [];
}

export async function insertKitchenTipout(t: {
  date: string;
  employee_name: string;
  employee_id?: string;
  amount: number;
  source?: string;
}): Promise<KitchenTipout> {
  const { data, error } = await supabase
    .from("tips_kitchen_tipouts")
    .insert(t)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function replaceKitchenTipoutsForEvent(
  date: string,
  eventName: string,
  entries: Array<{ employee_id: string; employee_name: string; amount: number }>,
): Promise<void> {
  const { error: delErr } = await supabase
    .from("tips_kitchen_tipouts")
    .delete()
    .eq("date", date)
    .eq("source", eventName);
  if (delErr) throw delErr;

  const rows = entries
    .filter((e) => e.amount > 0)
    .map((e) => ({
      date,
      employee_id: e.employee_id,
      employee_name: e.employee_name,
      amount: e.amount,
      source: eventName,
    }));
  if (rows.length === 0) return;

  const { error: insErr } = await supabase
    .from("tips_kitchen_tipouts")
    .insert(rows);
  if (insErr) throw insErr;
}

// ── Settings ───────────────────────────────────────────────

export async function fetchSettings(): Promise<TipsSetting[]> {
  const { data, error } = await supabase.from("tips_settings").select("*");
  if (error) throw error;
  return data ?? [];
}

export async function updateSetting(key: string, value: string): Promise<void> {
  const { error } = await supabase
    .from("tips_settings")
    .upsert({ key, value }, { onConflict: "key" });
  if (error) throw error;
}

export function getSettingValue(settings: TipsSetting[], key: string, fallback: string): string {
  return settings.find((s) => s.key === key)?.value ?? fallback;
}

// ── Import Logs ────────────────────────────────────────────

export async function fetchImportLogs(): Promise<ImportLog[]> {
  const { data, error } = await supabase
    .from("tips_import_logs")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function insertImportLog(log: Omit<ImportLog, "id" | "created_at">): Promise<ImportLog> {
  const { data, error } = await supabase
    .from("tips_import_logs")
    .insert(log)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Axe coach history.
 *
 * Coach tip entry moved into the Square tip-share sheet in June 2026, so these
 * tables are read-only now: they still hold the coach tips recorded before the
 * cutover, which the combined report folds in so older periods stay complete.
 */

export async function fetchCoachDailyTips(from: string, to: string): Promise<CoachDailyTip[]> {
  const { data, error } = await supabase
    .from("tips_coach_daily_tips")
    .select("*")
    .gte("date", from)
    .lte("date", to)
    .order("date");
  if (error) throw error;
  return data ?? [];
}

export async function fetchCoachEvents(from: string, to: string): Promise<CoachEvent[]> {
  const { data, error } = await supabase
    .from("tips_coach_events")
    .select("*")
    .gte("date", from)
    .lte("date", to)
    .order("date");
  if (error) throw error;
  return data ?? [];
}

export async function fetchCoachEventAssignments(eventIds: string[]): Promise<CoachEventAssignment[]> {
  if (eventIds.length === 0) return [];
  const { data, error } = await supabase
    .from("tips_coach_event_assignments")
    .select("*")
    .in("event_id", eventIds);
  if (error) throw error;
  return data ?? [];
}

// ── Square Tip Share ──────────────────────────────────────

export async function fetchSquareTipShare(payPeriodId: string): Promise<SquareTipShare | null> {
  const { data, error } = await supabase
    .from("tips_square_tip_shares")
    .select("*")
    .eq("pay_period_id", payPeriodId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertSquareTipShare(share: {
  pay_period_id: string;
  total_tips: number;
  processing_fee_pct: number;
}): Promise<SquareTipShare> {
  const { data, error } = await supabase
    .from("tips_square_tip_shares")
    .upsert(
      { ...share, updated_at: new Date().toISOString() },
      { onConflict: "pay_period_id" },
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchSquareTipShareEntries(payPeriodId: string): Promise<SquareTipShareEntry[]> {
  const { data, error } = await supabase
    .from("tips_square_tip_share_entries")
    .select("*")
    .eq("pay_period_id", payPeriodId);
  if (error) throw error;
  return data ?? [];
}

export async function upsertSquareTipShareEntriesBatch(rows: Array<{
  pay_period_id: string;
  employee_id: string;
  hours_worked: number;
  calculated_share: number;
  extra_tips: number;
  event_tips_detail: number[];
}>): Promise<void> {
  const payload = rows.map((r) => ({ ...r, updated_at: new Date().toISOString() }));
  const { error } = await supabase
    .from("tips_square_tip_share_entries")
    .upsert(payload, { onConflict: "pay_period_id,employee_id" });
  if (error) throw error;
}
